<template>
  <v-data-table
    disable-pagination
    disable-sort
    hide-default-footer
    :headers="headers"
    :items="rows"
    :height="height"
    :loading="loading"
    loading-text="Fetching..."
    class="database-table"
  >
    <template v-slot:item="{ item }">
      <QuoteExpandableTableRow
        :item="item"
        :isRowExpanded="isRowExpanded(item.id)"
        @toggle="(item) => toggleRow(item.id)"
      />
      <template v-if="isRowExpanded(item.id)">
        <template v-for="(group, groupName) in item.groupedQuotes">
          <QuoteFolderHeader
            :key="item.id + '_' + groupName"
            :name="groupName"
            :title="group[0].title"
            :count="group.length"
            :expanded="isGroupExpanded(item.id, groupName)"
            @toggle="toggleGroup(item.id, groupName)"
          />
          <template v-if="isGroupExpanded(item.id, groupName)">
            <QuoteTableRow
              v-for="quote in group"
              :key="quote.id"
              :item="quote"
              class="database-row"
              @click.native="goToPage(quote)"
            />
          </template>
        </template>
      </template>
    </template>
  </v-data-table>
</template>

<script>
import QuoteTableRow from "./_QuoteTableRow";
import QuoteExpandableTableRow from "./_QuoteExpandableTableRow";
import QuoteFolderHeader from "./_QuoteFolderHeader";
export default {
  props: {
    query: Object,
    height: [Number, String],
  },
  components: {
    QuoteTableRow,
    QuoteExpandableTableRow,
    QuoteFolderHeader,
  },
  data() {
    return {
      headers: [
        { text: "", width: "45px" },
        { text: "", width: "45px" },
        { text: "Client", width: "18%" },
        { text: "Nights", width: "40px" },
        { text: "Title", width: "32%" },
        { text: "City Start", width: "120px" },
        { text: "City End", width: "120px" },
        { text: "Date", width: "26%" },
        { text: "Reference", width: "24%" },
        { text: "Version", width: "40px" },
      ],
      rows: [],
      loading: true,
      queryActive: true,
      expandedRows: [],
      expandedGroups: [], // Stores keys like "ClientId_GroupName"
    };
  },
  watch: {
    query(searchQuery) {
      if (this.queryActive) return;

      this.queryDatabase(searchQuery);
    },
    "$route.query.clients"() {
      if (this.$route.name !== "quotes" || this.loading) return;
      this.restoreExpansionFromRoute();
    },
    "$route.query.folders"() {
      if (this.$route.name !== "quotes" || this.loading) return;
      this.restoreExpansionFromRoute();
    },
  },
  methods: {
    goToPage(item) {
      this.syncExpansionToRoute();
      this.$router.push({
        name: "quotes_view",
        params: {
          quote_id: item.id,
        },
      });
    },
    syncExpansionToRoute() {
      if (this.$route.name !== "quotes") return;

      const query = { ...this.$route.query };

      if (this.expandedRows.length) {
        query.clients = this.expandedRows.map(encodeURIComponent).join(",");
      } else {
        delete query.clients;
      }

      if (this.expandedGroups.length) {
        query.folders = this.expandedGroups.map(encodeURIComponent).join(",");
      } else {
        delete query.folders;
      }

      this.$router.replace({ name: "quotes", query }).catch(() => {});
    },
    restoreExpansionFromRoute() {
      const { clients, folders } = this.$route.query;

      if (clients) {
        const clientNames = String(clients)
          .split(",")
          .map((name) => decodeURIComponent(name))
          .filter((name) => this.rows.some((row) => row.id === name));
        this.expandedRows = clientNames;
      } else {
        this.expandedRows = [];
      }

      if (folders) {
        const folderKeys = String(folders)
          .split(",")
          .map((key) => decodeURIComponent(key))
          .filter((key) =>
            this.rows.some((row) => Object.keys(row.groupedQuotes || {}).some((groupName) => `${row.id}_${groupName}` === key))
          );
        this.expandedGroups = folderKeys;

        folderKeys.forEach((key) => {
          const row = this.rows.find((item) =>
            Object.keys(item.groupedQuotes || {}).some((groupName) => `${item.id}_${groupName}` === key)
          );
          if (row && !this.expandedRows.includes(row.id)) {
            this.expandedRows.push(row.id);
          }
        });
      } else {
        this.expandedGroups = [];
      }
    },
    queryDatabase(searchQuery) {
      this.loading = true;
      this.queryActive = true;

      return this.$store
        .dispatch("QuoteStore/getQuoteList", { query: searchQuery })
        .then((rows) => {
          this.loading = false;
          this.queryActive = false;

          let groupedQuotes = [];

          // First, group by Client Name as before
          const sortedRows = rows
            .map((row) => ({ ...row, client_name: `${row.client_name}` }))
            .sort((a, b) => a.client_name.localeCompare(b.client_name));

          let currentClientGroup = [];

          sortedRows.forEach((currentRow, i) => {
            if (i == 0 || currentRow.client_name == sortedRows[i - 1].client_name) {
              currentClientGroup.push(currentRow);
            } else {
              groupedQuotes.push(this.createClientGroupObject(sortedRows[i - 1].client_name, currentClientGroup));
              currentClientGroup = [currentRow];
            }
          });

          if (currentClientGroup.length > 0) {
            groupedQuotes.push(this.createClientGroupObject(currentClientGroup[0].client_name, currentClientGroup));
          }

          this.rows = groupedQuotes;
          this.restoreExpansionFromRoute();
        })
        .catch((err) => {
          this.loading = false;
          this.$root.$error(err);
        });
    },
    createClientGroupObject(clientName, quotes) {
      const sortedQuotes = this.sortQuotes(quotes);

      // Group by folder
      const groupedByFolder = sortedQuotes.reduce((groups, quote) => {
        const key = quote.folder || "ungrouped";
        if (!groups[key]) groups[key] = [];
        groups[key].push(quote);
        return groups;
      }, {});

      // Sort keys: by minimum nights, but "ungrouped" always last
      const orderedGroups = Object.keys(groupedByFolder)
        .sort((a, b) => {
          if (a === "ungrouped") return 1;
          if (b === "ungrouped") return -1;
          const minA = groupedByFolder[a][0].number_of_nights || 0;
          const minB = groupedByFolder[b][0].number_of_nights || 0;
          if (minA !== minB) {
            return minA - minB;
          }
          return a.localeCompare(b);
        })
        .reduce((obj, key) => {
          obj[key] = groupedByFolder[key];
          return obj;
        }, {});

      return {
        id: clientName,
        client_name: clientName,
        quotes: sortedQuotes,
        groupedQuotes: orderedGroups,
      };
    },
    sortQuotes(quotes) {
      return quotes.sort((a, b) => (a.number_of_nights || 0) - (b.number_of_nights || 0));
    },
    toggleRow(id) {
      const index = this.expandedRows.indexOf(id);
      if (index === -1) {
        this.expandedRows.push(id);
      } else {
        this.expandedRows.splice(index, 1);
        const row = this.rows.find((item) => item.id === id);
        if (row) {
          const clientGroupKeys = Object.keys(row.groupedQuotes || {}).map((groupName) => `${id}_${groupName}`);
          this.expandedGroups = this.expandedGroups.filter((key) => !clientGroupKeys.includes(key));
        }
      }
      this.syncExpansionToRoute();
    },
    isRowExpanded(id) {
      return this.expandedRows.includes(id);
    },
    toggleGroup(clientId, groupName) {
      const key = `${clientId}_${groupName}`;
      const index = this.expandedGroups.indexOf(key);
      if (index === -1) {
        this.expandedGroups.push(key);
      } else {
        this.expandedGroups.splice(index, 1);
      }
      this.syncExpansionToRoute();
    },
    isGroupExpanded(clientId, groupName) {
      const key = `${clientId}_${groupName}`;
      return this.expandedGroups.includes(key);
    },
    toggleAllRows() {
      if (this.expandedRows.length === this.items.length) {
        this.expandedRows = [];
      } else {
        this.expandedRows = this.items.map((item) => item.id);
      }
    },
  },
  mounted() {
    this.queryDatabase(this.query);
  },
};
</script>
